import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { addCommitment, removeCommitment, updateCommitment } from '@/features/planning/api'
import { fetchProjects } from '@/features/portfolio/api'
import type { ApiError } from '@/lib/api/client'
import type { Project } from '@/types/domain'
import type { Plan } from '@/types/planning'

/**
 * The projects a division proposes to run this period, against the capacity it
 * actually has.
 *
 * Every write returns the recalculated capacity, so adding a project past what
 * the division can deliver updates the overflow immediately — before the plan
 * is committed rather than after the quarter is lost.
 */
export function CommitmentBoard({ plan }: { plan: Plan }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [projectId, setProjectId] = useState('')
  const [days, setDays] = useState('10')
  const [reason, setReason] = useState('')

  const projects = useQuery<Project[], ApiError>({
    queryKey: ['projects'],
    queryFn: () => fetchProjects(),
    retry: false,
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['plan', plan.id] })

  // A committed period is immutable without a reason on the record, so every
  // mutation from this board carries one when there is one to carry.
  const withReason = <T extends object>(input: T): T & { reason?: string } =>
    plan.isEditable || reason.trim() === '' ? input : { ...input, reason: reason.trim() }

  const add = useMutation({
    mutationFn: () =>
      addCommitment(plan.id, withReason({ projectId, committedDays: Number(days) || 0 })),
    onSuccess: () => {
      setProjectId('')
      void invalidate()
    },
  })

  const change = useMutation({
    mutationFn: (input: { id: string; committedDays: number }) =>
      updateCommitment(plan.id, input.id, withReason({ committedDays: input.committedDays })),
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: (id: string) =>
      removeCommitment(plan.id, id, plan.isEditable ? undefined : reason.trim() || undefined),
    onSuccess: invalidate,
  })

  const committedIds = new Set(plan.commitments.map((commitment) => commitment.projectId))
  const available = (projects.data ?? []).filter(
    (project) =>
      !committedIds.has(project.id) &&
      (plan.divisionId === null || project.divisionId === plan.divisionId),
  )

  const busy = add.isPending || change.isPending || remove.isPending

  return (
    <section>
      <h2 className="text-ink-900 mb-3 text-sm font-semibold">{t('planning.commitments')}</h2>

      {plan.commitments.length === 0 ? (
        <p className="border-line bg-surface text-ink-700 rounded-md border p-4 text-sm">
          {t('planning.noCommitments')}
        </p>
      ) : (
        <ul className="border-line bg-surface divide-line divide-y rounded-md border">
          {plan.commitments.map((commitment) => (
            <li
              key={commitment.id}
              className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-3 px-4 py-3"
            >
              <div className="min-w-0">
                <span className="text-brand-600 font-mono text-xs">{commitment.projectKey}</span>
                <div className="text-ink-900 truncate text-sm">{commitment.projectName}</div>
              </div>

              <label className="flex items-center gap-1.5">
                <input
                  type="number"
                  min={0}
                  defaultValue={commitment.committedDays}
                  disabled={plan.status === 'closed' || busy}
                  onBlur={(event) => {
                    const value = Number(event.target.value) || 0
                    if (value !== commitment.committedDays) {
                      change.mutate({ id: commitment.id, committedDays: value })
                    }
                  }}
                  className="border-line w-20 rounded border px-2 py-1 text-end font-mono text-sm tabular-nums"
                />
                <span className="text-ink-500 text-xs">{t('planning.days')}</span>
              </label>

              <button
                type="button"
                disabled={plan.status === 'closed' || busy}
                onClick={() => remove.mutate(commitment.id)}
                className="text-ink-500 hover:text-ink-900 text-xs underline disabled:opacity-40"
              >
                {t('access.revoke')}
              </button>
            </li>
          ))}
        </ul>
      )}

      {plan.status === 'closed' ? null : (
        <form
          className="border-line bg-surface mt-3 flex flex-wrap items-end gap-3 rounded-md border p-4"
          onSubmit={(event) => {
            event.preventDefault()
            if (projectId !== '') add.mutate()
          }}
        >
          <label className="min-w-56 flex-1">
            <span className="text-ink-500 mb-1 block font-mono text-[10px] tracking-widest uppercase">
              {t('planning.chooseProject')}
            </span>
            <select
              value={projectId}
              onChange={(event) => setProjectId(event.target.value)}
              className="border-line bg-surface w-full rounded border px-2.5 py-1.5 text-sm"
            >
              <option value="">—</option>
              {available.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.key} · {project.name}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span className="text-ink-500 mb-1 block font-mono text-[10px] tracking-widest uppercase">
              {t('planning.days')}
            </span>
            <input
              type="number"
              min={0}
              value={days}
              onChange={(event) => setDays(event.target.value)}
              className="border-line w-24 rounded border px-2.5 py-1.5 text-end font-mono text-sm tabular-nums"
            />
          </label>

          <button
            type="submit"
            disabled={projectId === '' || busy}
            className="bg-brand-600 rounded px-3 py-1.5 text-sm font-medium text-white disabled:opacity-40"
          >
            {t('planning.addCommitment')}
          </button>

          {plan.isEditable ? null : (
            <label className="w-full">
              <span className="text-ink-700 mb-1 block text-xs">
                {t('planning.reasonExplainer')}
              </span>
              <input
                type="text"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder={t('planning.reason')}
                className="border-line w-full rounded border px-2.5 py-1.5 text-sm"
              />
            </label>
          )}
        </form>
      )}
    </section>
  )
}
